import React, { useEffect, useRef, useState } from 'react';
import { Modal, notification } from 'antd';
import PropertyService from 'service/PropertyService';
import BookingService from 'service/BookingService';
import PricingService from 'service/PricingService';
import { transformBookingSettingsFromApi } from 'library/helpers/bookingSettingsFromApi';
import {
  isFlexibleCheckinAmenityName,
  isPoolAmenityName,
} from 'library/helpers/amenityKinds';
import { IMAGE_CATEGORY_OPTIONS } from 'settings/constant';
import { Field, Grid, MultiChips, TextArea, TextInput } from 'components/Onboarding';
import {
  CUSTOM_PREFIX,
  CUSTOM_RULES_LIMIT,
  HOUSE_RULE_OPTIONS,
  QUIET_PREFIX,
  parseRestrictions,
} from 'containers/HouseRules/HouseRules';
import {
  parseWeekendDays,
  serializeWeekendDays,
} from 'containers/CalendarPricing/CalendarPricing';

export type MissingItemKey =
  | 'pool'
  | 'flex'
  | 'photos'
  | 'video'
  | 'walkthrough'
  | 'weekendDays'
  | 'weekendPrice'
  | 'refundable'
  | 'rules';

interface Props {
  itemKey: MissingItemKey | null;
  title: string;
  propertyId: number | string;
  onClose: () => void;
  /** Called after a successful save so the list can refresh. */
  onSaved: (key: MissingItemKey) => void;
}

const FLEX_OPTIONS: { value: string; label: string }[] = [
  { value: 'self', label: 'Self check-in' },
  { value: 'assisted', label: 'Assisted' },
  { value: 'front_desk', label: 'Front desk' },
  { value: 'digital', label: 'Digital / keyless' },
];

const WEEKEND_LABELS: { key: 'fri' | 'sat' | 'sun'; label: string }[] = [
  { key: 'fri', label: 'Friday' },
  { key: 'sat', label: 'Saturday' },
  { key: 'sun', label: 'Sunday' },
];

const MAX_VIDEO_MB = 50;
const MAX_PHOTO_BYTES = 5 * 1024 * 1024;

const digits = (value: string): string => value.replace(/[^\d.]/g, '');

const formatDate = (d: Date): string => {
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
};

const isVideoSuccess = (res: any): boolean => {
  if (!res) return false;
  const code = res.responseInformation?.responseCode;
  if (Number(code) === 0) return true;
  if (res.responseInformation?.isSuccess === true || res.success === true || res.result === true) {
    return true;
  }
  const r = res.result;
  return !!(r && typeof r === 'object' && !Array.isArray(r) && r.videoUrl);
};

/**
 * Add / update one optional detail of an imported listing without leaving the Verify screen.
 * Each form saves the same fields, through the same APIs, as that detail's normal page.
 */
const MissingItemModal: React.FC<Props> = ({ itemKey, title, propertyId, onClose, onSaved }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // pool
  const [poolLength, setPoolLength] = useState('');
  const [poolBreadth, setPoolBreadth] = useState('');
  const [poolDepth, setPoolDepth] = useState('');
  // flexible check-in
  const [flexType, setFlexType] = useState('');
  // videos / photos
  const [file, setFile] = useState<File | null>(null);
  const [photoFiles, setPhotoFiles] = useState<File[]>([]);
  const [photoCategory, setPhotoCategory] = useState('livingRoom');
  // pricing
  const [weekendDays, setWeekendDays] = useState({ fri: false, sat: true, sun: true });
  const [weekendPrice, setWeekendPrice] = useState('');
  const [deposit, setDeposit] = useState('');
  // house rules
  const [rules, setRules] = useState<string[]>([]);
  const [quietHours, setQuietHours] = useState('');
  const [customRules, setCustomRules] = useState('');
  const settingsRef = useRef<Record<string, any> | null>(null);

  const open = itemKey !== null;

  // Load what is saved now, so the form opens showing it.
  useEffect(() => {
    if (!itemKey) return undefined;
    setError('');
    setFile(null);
    setPhotoFiles([]);
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        if (itemKey === 'pool' || itemKey === 'flex') {
          const res: any = await PropertyService.getPropertyDetailsById(propertyId);
          if (cancelled) return;
          const server = res?.result || {};
          const meta = server.propertyMetadata || {};
          const amenities: any[] = server.amenities || [];
          if (itemKey === 'pool') {
            const note = String(
              amenities.find((a) => isPoolAmenityName(String(a?.name || '')))?.description || '',
            );
            const dim = (label: string): string =>
              (note.match(new RegExp(`${label}:\\s*([^,]+)`, 'i'))?.[1] || '').trim();
            setPoolLength(String(meta.poolLength || dim('Length')));
            setPoolBreadth(String(meta.poolBreadth || dim('Width')));
            setPoolDepth(String(meta.poolDepth || dim('Depth')));
          } else {
            const note = String(
              amenities.find((a) => isFlexibleCheckinAmenityName(String(a?.name || '')))
                ?.description || '',
            )
              .toLowerCase()
              .replace(/[-\s]+/g, '_');
            const current = String(meta.flexibleCheckinType || note);
            setFlexType(FLEX_OPTIONS.some((o) => o.value === current) ? current : '');
          }
        } else if (itemKey === 'weekendDays' || itemKey === 'weekendPrice' || itemKey === 'refundable') {
          const res: any = await PricingService.getPropertyPricingDetails(propertyId);
          if (cancelled) return;
          const result = res?.result || {};
          setWeekendDays(parseWeekendDays(result.weekendDays));
          setWeekendPrice(result.weekendDefaultPrice ? String(result.weekendDefaultPrice) : '');
          setDeposit(result.securityDeposit ? String(result.securityDeposit) : '');
        } else if (itemKey === 'rules') {
          const res: any = await PropertyService.getBookingSettingsByPropertyId(propertyId);
          if (cancelled) return;
          const settings = res?.result ? transformBookingSettingsFromApi(res.result) : null;
          settingsRef.current = settings;
          const parsed = parseRestrictions(settings?.restrictions || []);
          setRules(parsed.selected);
          setQuietHours(parsed.quietHours);
          setCustomRules(parsed.customRules);
        }
      } catch (_) {
        if (!cancelled) setError('Could not load the current details. You can still add them.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [itemKey, propertyId]);

  /** Re-saves the property with some amenity note / metadata changed, like the Amenities page does. */
  const saveAmenityDetail = async (
    matches: (name: string) => boolean,
    description: string,
    metadata: Record<string, string>,
    cacheValues: Record<string, string>,
  ): Promise<void> => {
    const [propRes, amenityList]: any[] = await Promise.all([
      PropertyService.getPropertyDetailsById(propertyId),
      PropertyService.getAmenities(),
    ]);
    const server = propRes?.result;
    if (!server) throw new Error('Property not found');
    const amenities: any[] = [...(server.amenities || [])];
    const at = amenities.findIndex((a) => matches(String(a?.name || '')));
    if (at >= 0) {
      amenities[at] = { ...amenities[at], description };
    } else {
      const def = (amenityList?.result || []).find((a: any) => matches(String(a.name)));
      if (!def) throw new Error('Amenity not found');
      amenities.push({ id: def.id, name: def.name, description });
    }
    const res: any = await PropertyService.updateProperty({
      ...server,
      propertyId,
      amenities,
      propertyMetadata: { ...(server.propertyMetadata || {}), ...metadata },
    });
    if (Number(res?.responseInformation?.responseCode) !== 0) throw new Error('Not saved');
    try {
      const key = `amenityFollowUp_${propertyId}`;
      const cached = JSON.parse(localStorage.getItem(key) || '{}') || {};
      localStorage.setItem(key, JSON.stringify({ ...cached, ...cacheValues }));
    } catch (_) {
      /* storage unavailable */
    }
  };

  const saveVideo = async (kind: 'main' | 'walkthrough'): Promise<void> => {
    if (!file) throw new Error('no file');
    const pid = String(propertyId);
    let existing: string | null = null;
    let stage = 3;
    try {
      const current: any = await PropertyService.getPropertyVideo(pid);
      if (Number(current?.responseInformation?.responseCode) === 0) {
        existing =
          (kind === 'main' ? current?.result?.videoUrl : current?.result?.walkthroughVideoUrl) ?? null;
      }
      const prop: any = await PropertyService.getPropertyDetailsById(pid);
      stage = Math.max(Number(prop?.result?.onboardingStage) || 0, 3);
    } catch (_) {
      existing = null;
    }
    const res: any = existing
      ? await PropertyService.videoUpdate({
          addVideo: file,
          deletedVideoUrl: existing,
          propertyId: pid,
          videoType: kind,
        } as any)
      : await PropertyService.videoUpload({
          video: file,
          onboardingStage: stage,
          propertyId: pid,
          videoType: kind,
        } as any);
    if (!isVideoSuccess(res)) throw new Error('Video not saved');
  };

  const savePhotos = async (): Promise<void> => {
    const pid = String(propertyId);
    const current: any = await PropertyService.getPhotos(pid);
    const existing: Record<string, string[]> = current?.result?.imageUrlList || {};
    const existingUpdates: Record<string, string[]> = {};
    Object.keys(existing).forEach((cat) => {
      existingUpdates[cat] = Array.from(new Set((existing[cat] || []).filter(Boolean)));
    });
    const res: any = await PropertyService.updatePhotos(
      { [photoCategory]: photoFiles },
      existingUpdates,
      {},
      null,
      [],
      pid,
    );
    if (!res?.result) throw new Error('Photos not saved');
  };

  /** Pricing needs the whole calendar re-sent; only the weekend fields / deposit change. */
  const savePricing = async (): Promise<void> => {
    const res: any = await PricingService.getPropertyPricingDetails(propertyId);
    const result = res?.result || {};
    const defaultPrice: number | undefined =
      typeof result.defaultPrice === 'number' ? result.defaultPrice : undefined;
    const oldWeekendPrice: number | undefined =
      typeof result.weekendDefaultPrice === 'number' ? result.weekendDefaultPrice : undefined;
    const newWeekendDays =
      itemKey === 'weekendDays' ? weekendDays : parseWeekendDays(result.weekendDays);
    const newWeekendPrice =
      itemKey === 'weekendPrice' ? Number(weekendPrice) : oldWeekendPrice;
    const newDeposit =
      itemKey === 'refundable'
        ? Number(deposit)
        : typeof result.securityDeposit === 'number'
        ? result.securityDeposit
        : undefined;

    // Same rule the pricing page applies: weekend nights that still carry the default (or the old
    // weekend) price move to the new weekend price; custom prices the host set are left alone.
    const calendar: Record<string, number> = {};
    (Array.isArray(result.propertyCalendarPrices) ? result.propertyCalendarPrices : []).forEach(
      (p: any) => {
        if (p?.priceDate && p?.pricePerNight != null) calendar[String(p.priceDate)] = Number(p.pricePerNight);
      },
    );
    if (newWeekendPrice != null && defaultPrice != null) {
      const dayNumbers = [
        ...(newWeekendDays.fri ? [5] : []),
        ...(newWeekendDays.sat ? [6] : []),
        ...(newWeekendDays.sun ? [0] : []),
      ];
      const start = new Date();
      const end = new Date(start);
      end.setMonth(start.getMonth() + 3);
      for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
        const key = formatDate(d);
        const current = calendar[key];
        if (dayNumbers.includes(d.getDay())) {
          if (current == null || current === defaultPrice || current === oldWeekendPrice) {
            calendar[key] = newWeekendPrice;
          }
        } else if (oldWeekendPrice != null && current === oldWeekendPrice) {
          calendar[key] = defaultPrice;
        }
      }
    }
    const propertyCalendarPrices = Object.entries(calendar).map(([priceDate, pricePerNight]) => ({
      priceDate,
      pricePerNight,
    }));
    if (propertyCalendarPrices.length === 0) {
      throw new Error('Add your nightly price on the pricing page first.');
    }

    const saved: any = await PricingService.updatePropertyCalendarPricingDetails({
      propertyId,
      propertyCalendarPrices,
      discounts: [],
      defaultPrice,
      weekendDefaultPrice: newWeekendPrice,
      weekdayMinNights: result.weekdayMinNights ?? undefined,
      weekendMinNights: result.weekendMinNights ?? undefined,
      weekendDays: serializeWeekendDays(newWeekendDays),
      securityDeposit: newDeposit,
    });
    if (Number(saved?.responseInformation?.responseCode) !== 0) {
      throw new Error(saved?.responseInformation?.responseMessage || 'Pricing not saved');
    }
  };

  const saveRules = async (): Promise<void> => {
    const base = settingsRef.current;
    if (!base?.checkInTime || !base?.checkOutTime) throw new Error('Booking settings missing');
    const restrictions: string[] = rules.map((rule) =>
      rule === QUIET_PREFIX && quietHours.trim() ? `${QUIET_PREFIX}: ${quietHours.trim()}` : rule,
    );
    if (customRules.trim()) restrictions.push(`${CUSTOM_PREFIX}${customRules.trim()}`);
    const prop: any = await PropertyService.getPropertyDetailsById(propertyId);
    const stage = Math.max(Number(prop?.result?.onboardingStage) || 0, 4);
    const res: any = await BookingService.savePropertyBookingSettings({
      propertyId,
      checkInTime: base.checkInTime,
      checkOutTime: base.checkOutTime,
      cancellationPolicy: JSON.stringify(base.cancellationPolicy),
      propertyPolicy: JSON.stringify(restrictions),
      partialPaymentAllowed: base.partialPaymentAllowed,
      partialPaymentPercentage: base.partialPaymentPercentage,
      onboardingStage: stage,
      approvalRequiredForBooking: false,
    });
    if (Number(res?.responseInformation?.responseCode) !== 0) throw new Error('Rules not saved');
  };

  const valid = ((): boolean => {
    switch (itemKey) {
      case 'pool':
        return !!(poolLength.trim() && poolBreadth.trim() && poolDepth.trim());
      case 'flex':
        return !!flexType;
      case 'video':
      case 'walkthrough':
        return !!file;
      case 'photos':
        return photoFiles.length > 0;
      case 'weekendDays':
        return weekendDays.fri || weekendDays.sat || weekendDays.sun;
      case 'weekendPrice':
        return Number(weekendPrice) > 0;
      case 'refundable':
        return Number(deposit) > 0;
      case 'rules':
        return rules.length > 0 || !!customRules.trim();
      default:
        return false;
    }
  })();

  const save = async (): Promise<void> => {
    if (!itemKey || !valid || saving) return;
    setSaving(true);
    setError('');
    try {
      if (itemKey === 'pool') {
        const parts = [
          `Length: ${poolLength.trim()}`,
          `Width: ${poolBreadth.trim()}`,
          `Depth: ${poolDepth.trim()}`,
        ];
        await saveAmenityDetail(
          isPoolAmenityName,
          parts.join(', '),
          {
            poolLength: poolLength.trim(),
            poolBreadth: poolBreadth.trim(),
            poolDepth: poolDepth.trim(),
          },
          {
            poolLength: poolLength.trim(),
            poolBreadth: poolBreadth.trim(),
            poolDepth: poolDepth.trim(),
          },
        );
      } else if (itemKey === 'flex') {
        await saveAmenityDetail(
          isFlexibleCheckinAmenityName,
          flexType,
          { flexibleCheckinType: flexType },
          { flexibleCheckinType: flexType },
        );
      } else if (itemKey === 'video') {
        await saveVideo('main');
      } else if (itemKey === 'walkthrough') {
        await saveVideo('walkthrough');
      } else if (itemKey === 'photos') {
        await savePhotos();
      } else if (itemKey === 'rules') {
        await saveRules();
      } else {
        await savePricing();
      }
      notification.success({ message: `${title} saved` });
      onSaved(itemKey);
      onClose();
    } catch (e: any) {
      setError(
        e?.message && !/^(Not saved|Property not found)$/.test(e.message)
          ? e.message
          : 'Couldn’t save. Please try again.',
      );
    } finally {
      setSaving(false);
    }
  };

  const pickVideo = (picked: File | undefined): void => {
    if (!picked) return;
    if (picked.size > MAX_VIDEO_MB * 1024 * 1024) {
      setError(`The video must be under ${MAX_VIDEO_MB}MB.`);
      return;
    }
    setError('');
    setFile(picked);
  };

  const pickPhotos = (picked: FileList | null): void => {
    const list = Array.from(picked || []);
    const tooBig = list.filter((f) => f.size > MAX_PHOTO_BYTES);
    if (tooBig.length) {
      setError(`Some photos are over 5MB: ${tooBig.map((f) => f.name).join(', ')}`);
      return;
    }
    setError('');
    setPhotoFiles(list);
  };

  const body = ((): React.ReactNode => {
    switch (itemKey) {
      case 'pool':
        return (
          <Grid $cols={3} $gap={12}>
            <Field label="Length (m)">
              <TextInput value={poolLength} inputMode="decimal" onChange={(e) => setPoolLength(digits(e.target.value))} />
            </Field>
            <Field label="Breadth (m)">
              <TextInput value={poolBreadth} inputMode="decimal" onChange={(e) => setPoolBreadth(digits(e.target.value))} />
            </Field>
            <Field label="Depth (m)">
              <TextInput value={poolDepth} inputMode="decimal" onChange={(e) => setPoolDepth(digits(e.target.value))} />
            </Field>
          </Grid>
        );
      case 'flex':
        return (
          <Field label="How do guests check in outside the usual hours?">
            <MultiChips
              options={FLEX_OPTIONS.map((o) => o.label)}
              values={FLEX_OPTIONS.filter((o) => o.value === flexType).map((o) => o.label)}
              onToggle={(label) =>
                setFlexType(FLEX_OPTIONS.find((o) => o.label === label)?.value || '')
              }
            />
          </Field>
        );
      case 'video':
      case 'walkthrough':
        return (
          <Field
            label={itemKey === 'video' ? 'Property video' : 'Walkthrough video'}
            hint={`MP4 or similar, up to ${MAX_VIDEO_MB}MB. A new video replaces the current one.`}
          >
            <input type="file" accept="video/*" onChange={(e) => pickVideo(e.target.files?.[0])} />
            {file ? <p style={{ margin: '8px 0 0', fontSize: 13 }}>{file.name}</p> : null}
          </Field>
        );
      case 'photos':
        return (
          <>
            <Field label="Room or area">
              <select
                value={photoCategory}
                onChange={(e) => setPhotoCategory(e.target.value)}
                style={{ height: 44, width: '100%', padding: '0 10px', borderRadius: 12, border: '1px solid #e5e7eb' }}
              >
                {Object.entries(IMAGE_CATEGORY_OPTIONS as Record<string, string>).map(([key, label]) => (
                  <option key={key} value={key}>
                    {label}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Photos" hint="JPG, PNG or WebP, up to 5MB each.">
              <input type="file" accept="image/*" multiple onChange={(e) => pickPhotos(e.target.files)} />
              {photoFiles.length ? (
                <p style={{ margin: '8px 0 0', fontSize: 13 }}>
                  {photoFiles.length} photo{photoFiles.length === 1 ? '' : 's'} selected
                </p>
              ) : null}
            </Field>
          </>
        );
      case 'weekendDays':
        return (
          <Field label="Which nights count as the weekend?">
            <MultiChips
              options={WEEKEND_LABELS.map((d) => d.label)}
              values={WEEKEND_LABELS.filter((d) => weekendDays[d.key]).map((d) => d.label)}
              onToggle={(label) => {
                const key = WEEKEND_LABELS.find((d) => d.label === label)?.key;
                if (key) setWeekendDays((cur) => ({ ...cur, [key]: !cur[key] }));
              }}
            />
          </Field>
        );
      case 'weekendPrice':
        return (
          <Field label="Price per night on weekends (₹)">
            <TextInput value={weekendPrice} inputMode="numeric" onChange={(e) => setWeekendPrice(digits(e.target.value))} />
          </Field>
        );
      case 'refundable':
        return (
          <Field label="Refundable security deposit (₹)" hint="Guests get this back after the stay.">
            <TextInput value={deposit} inputMode="numeric" onChange={(e) => setDeposit(digits(e.target.value))} />
          </Field>
        );
      case 'rules': {
        const options = [...HOUSE_RULE_OPTIONS, ...rules.filter((r) => !HOUSE_RULE_OPTIONS.includes(r))];
        return (
          <>
            <Field label="Rules and regulations">
              <MultiChips
                options={options}
                values={rules}
                onToggle={(rule) =>
                  setRules((cur) => (cur.includes(rule) ? cur.filter((r) => r !== rule) : [...cur, rule]))
                }
              />
            </Field>
            {rules.includes(QUIET_PREFIX) ? (
              <Field label="Quiet hours">
                <TextInput value={quietHours} placeholder="22:00 – 08:00" onChange={(e) => setQuietHours(e.target.value)} />
              </Field>
            ) : null}
            <Field label="Anything else?" hint={`${customRules.length}/${CUSTOM_RULES_LIMIT} characters`}>
              <TextArea
                value={customRules}
                maxLength={CUSTOM_RULES_LIMIT}
                style={{ minHeight: 96 }}
                onChange={(e) => setCustomRules(e.target.value)}
              />
            </Field>
          </>
        );
      }
      default:
        return null;
    }
  })();

  return (
    <Modal
      open={open}
      title={title}
      onCancel={saving ? undefined : onClose}
      onOk={save}
      okText="Save"
      okButtonProps={{ disabled: !valid || loading, loading: saving }}
      cancelButtonProps={{ disabled: saving }}
      destroyOnClose
      maskClosable={false}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16, opacity: loading ? 0.5 : 1 }}>
        {body}
        {error ? (
          <p role="alert" style={{ margin: 0, fontSize: 13, color: '#b45309' }}>
            {error}
          </p>
        ) : null}
      </div>
    </Modal>
  );
};

export default MissingItemModal;
